import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Check, Calendar as CalIcon, ArrowRight } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";

export default function StudioDetail() {
  const { id } = useParams();
  const [studio, setStudio] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [hours, setHours] = useState(2);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { api.get(`/studios/${id}`).then((r) => setStudio(r.data)); }, [id]);

  const dateKey = date ? date.toISOString().slice(0, 10) : "";
  const bookedSlots = useMemo(() => {
    if (!studio || !date) return [];
    return studio.booked_slots.filter((b) => b.booking_date === dateKey).map((b) => b.time_slot);
  }, [studio, dateKey, date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) { toast.error("Pick a date and time slot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studio-bookings", {
        studio_id: id, booking_date: dateKey, time_slot: slot, hours, ...form,
      });
      setSuccess(data);
      toast.success("Booking received! We'll confirm shortly.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Booking failed");
    } finally { setBusy(false); }
  };

  if (!studio) return <div className="pt-32 text-center">Loading studio…</div>;

  if (success) {
    return (
      <div className="pt-32 pb-24 container mx-auto px-4 max-w-2xl text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-brand grid place-items-center mb-6">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-display font-black text-4xl mb-3">Booking received!</h1>
        <p className="text-zinc-600 mb-8">
          We've received your request for <b>{success.studio_name}</b> on <b>{success.booking_date}</b> at <b>{success.time_slot}</b>.
          Our team will confirm via email/WhatsApp within 2 hours.
        </p>
        <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 mb-8 text-left">
          <div className="flex justify-between py-2 border-b border-zinc-200"><span>Hours</span><b>{success.hours}</b></div>
          <div className="flex justify-between py-2 border-b border-zinc-200"><span>Total</span><b>₹{success.total_amount.toLocaleString()}</b></div>
          <div className="flex justify-between py-2"><span>Status</span><b className="text-[#FF7F00]">Pending Payment</b></div>
        </div>
        <Link to="/studios" className="inline-flex px-6 py-3 rounded-full bg-[#0A1128] text-white font-semibold">Back to studios</Link>
      </div>
    );
  }

  return (
    <div data-testid="studio-detail-page" className="pt-24">
      <section className="container mx-auto px-4 pt-12 pb-16">
        <Link to="/studios" className="text-sm text-zinc-500 hover:text-[#FF007F]">← All studios</Link>
        <div className="grid lg:grid-cols-2 gap-10 mt-6">
          {/* Gallery */}
          <div className="grid grid-cols-2 gap-3">
            <img src={studio.images[0]} alt={studio.name} className="col-span-2 aspect-[16/10] object-cover rounded-2xl" />
            {studio.images.slice(1).map((im, i) => (
              <img key={i} src={im} alt={`${studio.name} ${i + 2}`} className="aspect-square object-cover rounded-2xl" />
            ))}
          </div>

          {/* Info */}
          <div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-3">{studio.name}</h1>
            <p className="text-lg text-[#52525B] mb-4">{studio.tagline}</p>
            <div className="flex items-center gap-2 text-sm text-zinc-500 mb-6"><MapPin className="w-4 h-4" />{studio.address}</div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {studio.amenities.map((a) => (
                <div key={a} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#00BFE0]" /> {a}
                </div>
              ))}
            </div>
            <div className="flex items-baseline gap-3 mb-6">
              <div className="font-display font-black text-3xl text-gradient">₹{studio.hourly_rate.toLocaleString()}/hr</div>
              <div className="text-sm text-zinc-500">or ₹{studio.daily_rate.toLocaleString()}/day</div>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section className="bg-zinc-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Book your slot</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mb-10">Pick a date, time & hours.</h2>

          <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
            {/* Step 1: Calendar */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
                <CalIcon className="w-4 h-4" /> Step 1 — Date
              </div>
              <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setSlot(""); }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} data-testid="booking-calendar" />
            </div>

            {/* Step 2: Slots */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Step 2 — Time slot</div>
              {!date && <div className="text-sm text-zinc-400">Pick a date first</div>}
              {date && (
                <div className="grid grid-cols-2 gap-2">
                  {studio.time_slots.map((t) => {
                    const taken = bookedSlots.includes(t);
                    return (
                      <button key={t} type="button" disabled={taken}
                        onClick={() => setSlot(t)} data-testid={`slot-${t}`}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border transition
                          ${slot === t ? "bg-[#0A1128] text-white border-[#0A1128]" : ""}
                          ${taken ? "bg-zinc-100 text-zinc-400 line-through cursor-not-allowed border-zinc-200" : "border-zinc-300 hover:border-[#00BFE0]"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="mt-5">
                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Hours</label>
                <div className="flex items-center gap-3 mt-2">
                  <button type="button" onClick={() => setHours(Math.max(1, hours - 1))} className="w-8 h-8 rounded-full border border-zinc-300">−</button>
                  <span className="font-display font-bold text-xl w-8 text-center">{hours}</span>
                  <button type="button" onClick={() => setHours(hours + 1)} className="w-8 h-8 rounded-full border border-zinc-300">+</button>
                </div>
                <div className="text-xs text-zinc-500 mt-2">Total ₹{(studio.hourly_rate * hours).toLocaleString()}</div>
              </div>
            </div>

            {/* Step 3: Details */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Step 3 — Your details</div>
              <div className="space-y-3">
                <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="booking-name" placeholder="Full name" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="booking-email" placeholder="Email" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="booking-phone" placeholder="Phone" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything we should know?" rows={3} className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              </div>
              <button type="submit" disabled={busy} data-testid="booking-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-brand text-white font-bold disabled:opacity-50">
                {busy ? "Submitting…" : <>Confirm Booking <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
